import { useInfiniteQuery, useMutation, UseMutationOptions, useQuery, useQueryClient } from 'react-query';
import {
  getActivities,
  getAgreementMagazines,
  getBenefits,
  getCheckPermission,
  getContract,
  getEconometer,
  getFaturas,
  CENTRAL_CLIENTE_PER_PAGE,
  getHasUnreadNotifications,
  getNotifications,
  getPromotionDetails,
  getPromotions,
  getPromotionsPaginated,
  getSales,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from './api';
import type { CentralClientePageParams } from './api';
import { RequestParameters } from '../types';
import { Params, PaginatedResponse } from '@/pages/Bookstore/types';
import { IMarkAllNotificationsReadResponse, IMarkNotificationReadResponse, INotificationItem } from './types';
import { useProfile } from '@/contexts/ProfileProvider';
import { buildMockContractResponse, buildMockFaturasResponse, isMockCentralClienteEmail } from './mockCentralCliente';

export enum ClubHook {
  Activities = 'activities',
  Sales = 'sales',
  Promotions = 'promotions',
  Benefits = 'benefits',
  PromotionDetails = 'promotionDetail',
  Econometer = 'econometer',
  CheckPermission = 'checkPermission',
  Notification = 'notifications',
  Contract = 'contract',
  Faturas = 'faturas',
  RevistasDigitais = 'revistasDigitais',
}

export function useActivities() {
  return useQuery([ClubHook.Activities], () => getActivities());
}

function centralClienteQueryOptions(enabled: boolean) {
  return {
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    keepPreviousData: true,
    enabled,
  };
}

export function useGetContract(params?: CentralClientePageParams) {
  const { profile } = useProfile();
  const isMockUser = isMockCentralClienteEmail(profile?.email);
  const page = params?.page ?? 1;
  const perPage = params?.per_page ?? CENTRAL_CLIENTE_PER_PAGE;

  return useQuery(
    [ClubHook.Contract, isMockUser, page, perPage],
    () => (isMockUser ? Promise.resolve(buildMockContractResponse(page)) : getContract({ page, per_page: perPage })),
    centralClienteQueryOptions(!!profile)
  );
}

export function useGetFaturas(params?: CentralClientePageParams) {
  const { profile } = useProfile();
  const isMockUser = isMockCentralClienteEmail(profile?.email);
  const page = params?.page ?? 1;
  const perPage = params?.per_page ?? CENTRAL_CLIENTE_PER_PAGE;

  return useQuery(
    [ClubHook.Faturas, isMockUser, page, perPage],
    () => (isMockUser ? Promise.resolve(buildMockFaturasResponse(page)) : getFaturas({ page, per_page: perPage })),
    centralClienteQueryOptions(!!profile)
  );
}

export function usePromotions(id?: number | null, params?: RequestParameters) {
  return useQuery([ClubHook.Promotions, params], () => getPromotions({ ...params, agreement: id }));
}

export function usePromotionsInfinity(name?: string, id?: number | null, params?: RequestParameters) {
  return useInfiniteQuery({
    queryKey: [name ? `promotions-${name}` : ClubHook.Promotions, name, params],
    queryFn: ({ pageParam = 1 }) => getPromotionsPaginated({ ...params, agreement_id: id, page: pageParam }),
    // Use the API's real `next` link so we stop once there are no more pages.
    getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length + 1 : undefined),
    refetchOnWindowFocus: false,
    enabled: !!id,
    retry: false,
  });
}

export function useNotificationsMutation(
  options: UseMutationOptions<PaginatedResponse<INotificationItem>, unknown, Params>
) {
  return useMutation((params: Params) => getNotifications(params), options);
}

export function useMarkNotificationAsRead(
  options?: UseMutationOptions<IMarkNotificationReadResponse, unknown, number>
) {
  const queryClient = useQueryClient();
  return useMutation((id: number) => markNotificationAsRead(id), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(['notifications-has-unread']);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useMarkAllNotificationsAsRead(
  options?: UseMutationOptions<IMarkAllNotificationsReadResponse, unknown, void>
) {
  const queryClient = useQueryClient();
  return useMutation(() => markAllNotificationsAsRead(), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(['notifications-has-unread']);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useHasUnreadNotifications(enabled: boolean = true) {
  return useQuery(['notifications-has-unread'], () => getHasUnreadNotifications(), {
    enabled,
    staleTime: 30_000,
  });
}

export function usePromotionDetails(id: string) {
  return useQuery([`${ClubHook.PromotionDetails}-${id}`, id], () => getPromotionDetails(id));
}

export function useHistory() {
  return useQuery([ClubHook.Econometer], () => getEconometer());
}

export function useSales(agreementId?: number) {
  return useQuery([ClubHook.Sales], () =>
    getSales({
      agreement: agreementId,
      page_size: 100,
    })
  );
}

export function useRevistasDigitaisInfinity(agreementId: number, params?: RequestParameters) {
  return useInfiniteQuery({
    queryKey: [ClubHook.RevistasDigitais, params],
    queryFn: ({ pageParam = 1 }) => getAgreementMagazines(agreementId, {
      page_size: params?.limit || 12,
      page: pageParam,
      ...params,
    }),
    getNextPageParam: (lastPage, allPages) => (lastPage.length > 0 ? allPages.length + 1 : undefined),
    refetchOnWindowFocus: false,
    enabled: !!agreementId,
    retry: false,
  });
}

export function useRevistasDigitais(agreementId: number, params?: RequestParameters) {
  return useQuery({
    queryKey: [ClubHook.RevistasDigitais, params, agreementId],
    queryFn: () => getAgreementMagazines(agreementId, {
      page_size: params?.limit || 10,
      ...params,
    }),
    refetchOnWindowFocus: false,
    enabled: !!agreementId,
    retry: false,
  });
}

export function useBenefits(params?: RequestParameters) {
  return useQuery([ClubHook.Benefits, params], () => getBenefits({ ...params, page_size: 100 }));
}

export function useCheckPermission(agreementId: number, productId?: number) {
  return useQuery(
    [ClubHook.CheckPermission, agreementId, productId],
    () => getCheckPermission({ agreement: agreementId, ...(productId && { product_id: productId }) }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      enabled: !!agreementId,
    }
  );
}
